import type { ReactNode } from 'react';

/**
 * The glyphs of the kit, drawn on a 24 × 24 grid. A glyph is stroked with the
 * current colour unless a shape says `fill`. To add one, add an entry: its key
 * becomes a valid `IconName` everywhere.
 */
const solid = { fill: 'currentColor', stroke: 'none' } as const;

/**
 * The logo: five bars on the 24 × 24 grid, as `[x, half of the height]`, centred on
 * y = 12. The one source of the mark: the favicon, the static front page and the
 * documentation draw the same bars (site/logo.test.ts checks them).
 */
export const LOGO_BARS: readonly (readonly [number, number])[] = [[3, 3], [7.5, 7], [12, 10], [16.5, 6], [21, 2]];

const bars = (heights: readonly (readonly [number, number])[]): ReactNode =>
  heights.map(([x, half]) => <line key={x} x1={x} y1={12 - half} x2={x} y2={12 + half} />);

export const ICONS = {
  play: <path d="M8 5v14l11-7z" {...solid} />,
  pause: (
    <>
      <rect x="6" y="5" width="4" height="14" rx="1" {...solid} />
      <rect x="14" y="5" width="4" height="14" rx="1" {...solid} />
    </>
  ),
  'play-circle': (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M10 8.5v7l6-3.5z" {...solid} />
    </>
  ),
  lock: (
    <>
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </>
  ),
  mic: (
    <>
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
    </>
  ),
  'mic-off': (
    <>
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0M12 18v3M4 4l16 16" />
    </>
  ),
  listening: (
    <>
      <circle cx="9" cy="8" r="3" />
      <path d="M3 20a6 6 0 0 1 12 0M17 6.5a4 4 0 0 1 0 6M19.5 4.5a7.5 7.5 0 0 1 0 10" />
    </>
  ),
  score: (
    <>
      <path d="M4 7h10M4 12h9M4 17h5M18.5 17.5V8l2.5 1" />
      <circle cx="16.5" cy="17.5" r="2" />
    </>
  ),
  check: <path d="M5 12.5l4.5 4.5L19 7.5" />,
  blocked: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M5.6 5.6l12.8 12.8" />
    </>
  ),
  note: (
    <>
      <path d="M10 17V5l8-2v12" />
      <circle cx="7.5" cy="17" r="2.5" />
      <circle cx="15.5" cy="15" r="2.5" />
    </>
  ),
  volume: <path d="M4 10v4h3l5 4V6L7 10zM15.5 9a4 4 0 0 1 0 6" />,
  speaker: <path d="M4 10v4h3l5 4V6L7 10zM15.5 9a4 4 0 0 1 0 6M18 6.5a8 8 0 0 1 0 11" />,
  repeat: <path d="M17 3l3 3-3 3M4 11V9a3 3 0 0 1 3-3h13M7 21l-3-3 3-3M20 13v2a3 3 0 0 1-3 3H4" />,
  timer: (
    <>
      <circle cx="12" cy="13" r="8" />
      <path d="M12 9v4l2.5 2M9 2h6" />
    </>
  ),
  waveform: bars([[4, 2], [8, 6], [12, 9], [16, 5], [20, 2]]),
  'check-circle': (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M8 12.5l2.7 2.7L16 9.5" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="M16 16l4.5 4.5" />
    </>
  ),
  // A speech bubble: a message to somebody (feedback).
  message: <path d="M5 4.5h14a1.5 1.5 0 0 1 1.5 1.5v9a1.5 1.5 0 0 1-1.5 1.5h-8l-4.5 3.5v-3.5H5A1.5 1.5 0 0 1 3.5 15V6A1.5 1.5 0 0 1 5 4.5zM8 9h8M8 12h5" />,
  eye: (
    <>
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  'eye-off': (
    <>
      <path d="M9.9 5.7A9.6 9.6 0 0 1 12 5.5c6 0 9.5 6.5 9.5 6.5a17 17 0 0 1-2.6 3.4M6.5 7.2C3.9 8.9 2.5 12 2.5 12S6 18.5 12 18.5a9.4 9.4 0 0 0 4.9-1.4" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2M3.5 3.5l17 17" />
    </>
  ),
  focus: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="3.5" />
    </>
  ),
  'arrow-right': <path d="M5 12h14M13 6l6 6-6 6" />,
  sine: <path d="M2 12c1.7-6 3.3-6 5 0s3.3 6 5 0 3.3-6 5 0 3.3 6 5 0" />,
  metronome: <path d="M9 3h6l4 18H5zM12 17l4-10M7 15h10" />,
  'tuning-fork': <path d="M8 3v7a4 4 0 0 0 8 0V3M12 14v7" />,
  shuffle: <path d="M16 4h4v4M4 18L20 4M16 20h4v-4M4 6l5 5M14.5 14.5L20 20" />,
  dictation: <path d="M4 6h12M4 11h8M4 16h5M13 20l1-4 5-5 3 3-5 5z" />,
  verified: <path d="M12 3l7 3v5c0 5-3 8.5-7 10-4-1.5-7-5-7-10V6zM8.5 12l2.5 2.5L15.5 10" />,
  logo: bars(LOGO_BARS),
  user: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </>
  ),
  copy: (
    <>
      <rect x="8" y="8" width="12" height="12" rx="2" />
      <path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" />
    </>
  ),
  download: <path d="M12 4v11M7 11l5 5 5-5M5 20h14" />,
  'chevron-down': <path d="M6 9l6 6 6-6" />,
  menu: <path d="M4 7h16M4 12h16M4 17h16" />,
  globe: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c2.5 2.6 3.8 5.6 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.6-3.8-9S9.500 5.600 12 3z" />
    </>
  ),
  logout: <path d="M14 4h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-4M10 16l-4-4 4-4M6 12h10" />,
  replay: <path d="M4 4v5h5M4.5 9a8 8 0 1 1-.5 5" />,
  speed: (
    <>
      <path d="M4 17a8.5 8.5 0 1 1 16 0M12 14l4-5" />
      <circle cx="12" cy="14" r="1" {...solid} />
    </>
  ),
  piano: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M9 13v6M15 13v6" />
      <rect x="7.5" y="5" width="3" height="8" {...solid} />
      <rect x="13.5" y="5" width="3" height="8" {...solid} />
    </>
  ),
  share: (
    <>
      <circle cx="6" cy="12" r="2.5" />
      <circle cx="18" cy="6" r="2.5" />
      <circle cx="18" cy="18" r="2.5" />
      <path d="M8.2 10.8l7.6-3.6M8.2 13.2l7.6 3.6" />
    </>
  ),
  overview: <path d="M12 6c-2-1.5-5-2-8-1.5v13c3-.5 6 0 8 1.5 2-1.5 5-2 8-1.5v-13c-3-.5-6 0-8 1.5zM12 6v13" />,
  scales: bars([[4, 5], [8, 9], [12, 3], [16, 7], [20, 4]]),
  player: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M10 9v6l5-3z" {...solid} />
    </>
  ),
  elements: (
    <>
      <rect x="4" y="4" width="6" height="6" rx="1" />
      <rect x="14" y="4" width="6" height="6" rx="1" />
      <rect x="4" y="14" width="6" height="6" rx="1" />
      <circle cx="17" cy="17" r="3" />
    </>
  ),
  palette: (
    <>
      <path d="M12 3a9 9 0 0 0 0 18c1.5 0 2-1 2-2 0-1.5 1-2 2.5-2H18a3 3 0 0 0 3-3c0-5-4-9-9-9z" />
      <circle cx="7.5" cy="12" r="1" {...solid} />
      <circle cx="10" cy="8" r="1" {...solid} />
      <circle cx="15" cy="8" r="1" {...solid} />
    </>
  ),
  help: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9.5a2.5 2.5 0 1 1 3.6 2.2c-.7.4-1.1.9-1.1 1.8" />
      <circle cx="12" cy="16.6" r="0.9" {...solid} />
    </>
  ),
  tune: (
    <>
      <path d="M4 7h10M18 7h2M4 12h4M12 12h8M4 17h12" />
      <circle cx="16" cy="7" r="2" />
      <circle cx="10" cy="12" r="2" />
      <circle cx="18" cy="17" r="2" />
    </>
  ),
  library: (
    <>
      <rect x="6" y="3" width="14" height="14" rx="2" />
      <path d="M4 7v12a2 2 0 0 0 2 2h12M13 12.5V7l3 1" />
      <circle cx="11.5" cy="12.5" r="1.5" />
    </>
  ),
} satisfies Record<string, ReactNode>;

export type IconName = keyof typeof ICONS;
export const ICON_NAMES = Object.keys(ICONS) as IconName[];
